/* ==========================================================================
   BERSHK Denim — theme.js
   Vanilla JS: header, drawers, AJAX cart, quick view, wishlist, Swiper init.
   ========================================================================== */
(() => {
  'use strict';

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const money = (cents) => {
    const cfg = window.theme || {};
    try {
      return new Intl.NumberFormat(cfg.locale || 'es', {
        style: 'currency',
        currency: cfg.currency || 'COP',
        maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(0);
    }
  };

  /* ------------------------------------------------------------------
     Header: hide on scroll down, solid on scroll
  ------------------------------------------------------------------ */
  function initHeader() {
    const header = $('.header');
    if (!header) return;
    let lastY = window.scrollY;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      const megaOpen = $('.mega.is-open');
      if (y > 120 && y > lastY && !megaOpen) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
      lastY = y;
    }, { passive: true });
  }

  /* ------------------------------------------------------------------
     Mega menu (desktop hover)
  ------------------------------------------------------------------ */
  function initMegaMenu() {
    const header = $('.header');
    if (!header) return;
    const items = $$('[data-mega-trigger]', header);
    const panels = $$('[data-mega-panel]', header);
    const closeAll = () => {
      panels.forEach((p) => p.classList.remove('is-open'));
      items.forEach((i) => i.closest('.header__nav-item')?.classList.remove('is-active'));
    };
    items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        closeAll();
        const panel = header.querySelector(`[data-mega-panel="${item.dataset.megaTrigger}"]`);
        if (panel) {
          panel.classList.add('is-open');
          item.closest('.header__nav-item')?.classList.add('is-active');
        }
      });
    });
    $$('.header__nav-item:not([data-has-mega])', header).forEach((el) =>
      el.addEventListener('mouseenter', closeAll));
    header.addEventListener('mouseleave', closeAll);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAll(); });
  }

  /* ------------------------------------------------------------------
     Drawers (cart, mobile menu) + search overlay
  ------------------------------------------------------------------ */
  const openDrawer = (name) => {
    const el = document.getElementById(`Drawer-${name}`);
    if (!el) return;
    el.classList.add('is-open');
    document.body.classList.add('lock-scroll');
    el.querySelector('.drawer')?.focus?.();
  };
  const closeDrawer = (name) => {
    const el = document.getElementById(`Drawer-${name}`);
    if (!el) return;
    el.classList.remove('is-open');
    if (!$('.drawer-root.is-open') && !$('.search-overlay.is-open')) {
      document.body.classList.remove('lock-scroll');
    }
  };

  function initDrawers() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) { e.preventDefault(); openDrawer(opener.dataset.drawerOpen); return; }
      const closer = e.target.closest('[data-drawer-close]');
      if (closer) { e.preventDefault(); closeDrawer(closer.dataset.drawerClose); return; }
      const searchOpen = e.target.closest('[data-search-open]');
      if (searchOpen) {
        e.preventDefault();
        $('.search-overlay')?.classList.add('is-open');
        document.body.classList.add('lock-scroll');
        setTimeout(() => $('.search-overlay__input')?.focus(), 80);
        return;
      }
      const searchClose = e.target.closest('[data-search-close]');
      if (searchClose) {
        $('.search-overlay')?.classList.remove('is-open');
        document.body.classList.remove('lock-scroll');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      $$('.drawer-root.is-open').forEach((el) => el.classList.remove('is-open'));
      $('.search-overlay.is-open')?.classList.remove('is-open');
      $('.quick-view.is-open') && closeQuickView();
      document.body.classList.remove('lock-scroll');
    });
  }

  /* ------------------------------------------------------------------
     AJAX cart
  ------------------------------------------------------------------ */
  async function refreshCartDrawer() {
    const res = await fetch(`${root}?sections=cart-drawer`);
    const data = await res.json();
    const holder = document.createElement('div');
    holder.innerHTML = data['cart-drawer'];
    const fresh = holder.querySelector('#Drawer-cart');
    const current = document.getElementById('Drawer-cart');
    if (fresh && current) {
      const wasOpen = current.classList.contains('is-open');
      current.innerHTML = fresh.innerHTML;
      if (wasOpen) current.classList.add('is-open');
      updateCartCount(current.querySelector('[data-cart-count-source]')?.dataset.cartCountSource);
    }
  }

  function updateCartCount(count) {
    if (count === undefined) return;
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.dataset.count = count;
    });
  }

  async function addToCart(body) {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'No se pudo añadir al carrito');
    }
    await refreshCartDrawer();
    openDrawer('cart');
  }

  function initCart() {
    // Product form submits (PDP + quick view)
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[data-ajax-cart]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = btn.dataset.addingText || '…'; }
      try {
        await addToCart(new FormData(form));
        closeQuickView();
      } catch (err) {
        alert(err.message);
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = label; }
      }
    });

    // Remove / change quantity inside the drawer (event delegation: drawer re-renders)
    document.addEventListener('click', async (e) => {
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: remove.dataset.cartRemove, quantity: 0 }),
        });
        await refreshCartDrawer();
      }
    });
  }

  /* ------------------------------------------------------------------
     Quick view
  ------------------------------------------------------------------ */
  let qvProduct = null;

  function closeQuickView() {
    $('#QuickView')?.classList.remove('is-open');
    if (!$('.drawer-root.is-open')) document.body.classList.remove('lock-scroll');
  }

  function qvSelectedVariant() {
    if (!qvProduct) return null;
    const selected = $$('#QuickView .option-btn.is-selected').map((b) => b.dataset.value);
    if (selected.length !== qvProduct.options.length) return null;
    return qvProduct.variants.find((v) =>
      selected.every((val, i) => v[`option${i + 1}`] === val)) || null;
  }

  function qvSync() {
    const variant = qvSelectedVariant();
    const input = $('#QuickView [name="id"]');
    const btn = $('#QuickView [type="submit"]');
    if (!btn) return;
    if (variant && variant.available) {
      if (input) input.value = variant.id;
      btn.disabled = false;
      btn.textContent = btn.dataset.addText;
    } else if (variant) {
      btn.disabled = true;
      btn.textContent = btn.dataset.soldoutText;
    } else {
      btn.disabled = true;
      btn.textContent = btn.dataset.chooseText;
    }
  }

  async function openQuickView(handle) {
    const modal = $('#QuickView');
    if (!modal) return;
    const res = await fetch(`${root}products/${handle}.js`);
    if (!res.ok) { window.location.href = `${root}products/${handle}`; return; }
    qvProduct = await res.json();
    const p = qvProduct;
    const strings = window.theme.strings || {};
    const onlyDefault = p.options.length === 1 && p.options[0] === 'Title';

    $('.quick-view__media', modal).innerHTML = p.featured_image
      ? `<img src="${p.featured_image}&width=720" alt="">` : '';
    $('.quick-view__title', modal).textContent = p.title;
    $('.quick-view__price', modal).innerHTML = p.compare_at_price > p.price
      ? `<span class="price price--lg price--sale">${money(p.price)}<s class="price__compare">${money(p.compare_at_price)}</s></span>`
      : `<span class="price price--lg">${money(p.price)}</span>`;
    $('.quick-view__link', modal).href = `${root}products/${p.handle}`;

    const optsHolder = $('.quick-view__options', modal);
    optsHolder.innerHTML = onlyDefault ? '' : p.options.map((name, i) => {
      const values = [...new Set(p.variants.map((v) => v[`option${i + 1}`]))];
      return `<div class="option-group" data-option-index="${i}">
        <div class="option-group__head"><p class="eyebrow">${name}</p></div>
        <div class="option-group__values">
          ${values.map((v) => `<button type="button" class="option-btn" data-value="${v}">${v}</button>`).join('')}
        </div>
      </div>`;
    }).join('');

    const idInput = $('#QuickView [name="id"]');
    if (onlyDefault && idInput) idInput.value = p.variants[0].id;
    const btn = $('#QuickView [type="submit"]');
    if (btn) {
      btn.disabled = !onlyDefault;
      btn.textContent = onlyDefault ? btn.dataset.addText : btn.dataset.chooseText;
    }

    optsHolder.querySelectorAll('.option-btn').forEach((b) => {
      b.addEventListener('click', () => {
        b.closest('.option-group__values').querySelectorAll('.option-btn')
          .forEach((x) => x.classList.remove('is-selected'));
        b.classList.add('is-selected');
        qvSync();
      });
    });

    modal.classList.add('is-open');
    document.body.classList.add('lock-scroll');
  }

  function initQuickView() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-view]');
      if (trigger) {
        e.preventDefault();
        openQuickView(trigger.dataset.quickView);
        return;
      }
      if (e.target.closest('[data-quick-view-close]')) closeQuickView();
    });
  }

  /* ------------------------------------------------------------------
     Wishlist (localStorage)
  ------------------------------------------------------------------ */
  const WISH_KEY = 'bershk:wishlist';
  const getWishlist = () => { try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; } catch (e) { return []; } };
  const setWishlist = (list) => localStorage.setItem(WISH_KEY, JSON.stringify(list));

  function paintWishlist() {
    const list = getWishlist();
    $$('[data-wishlist-toggle]').forEach((btn) => {
      btn.classList.toggle('is-wished', list.includes(btn.dataset.productId));
    });
    $$('[data-wishlist-count]').forEach((el) => {
      el.textContent = list.length;
      el.dataset.count = list.length;
    });
  }

  function initWishlist() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      const id = btn.dataset.productId;
      let list = getWishlist();
      list = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      setWishlist(list);
      paintWishlist();
    });
    paintWishlist();
  }

  /* ------------------------------------------------------------------
     Lookbook hotspots
  ------------------------------------------------------------------ */
  function initHotspots() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.hotspot__btn');
      if (btn) {
        const spot = btn.closest('.hotspot');
        const wasOpen = spot.classList.contains('is-open');
        $$('.hotspot.is-open').forEach((h) => h.classList.remove('is-open'));
        if (!wasOpen) spot.classList.add('is-open');
        return;
      }
      if (!e.target.closest('.hotspot')) {
        $$('.hotspot.is-open').forEach((h) => h.classList.remove('is-open'));
      }
    });
  }

  /* ------------------------------------------------------------------
     Product page: gallery, options, qty, sticky bar
  ------------------------------------------------------------------ */
  function initProductPage(scope = document) {
    const page = $('[data-product-page]', scope);
    if (!page) return;

    const variants = JSON.parse($('[data-variants-json]', page)?.textContent || '[]');

    // Gallery thumbnails
    const mainImg = $('.gallery__main img', page);
    $$('.gallery__thumb', page).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        $$('.gallery__thumb', page).forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (mainImg) {
          mainImg.src = thumb.dataset.src;
          mainImg.classList.remove('is-entering');
          void mainImg.offsetWidth;
          mainImg.classList.add('is-entering');
        }
      });
    });

    // Option selection → variant id
    const sync = () => {
      const selected = $$('.option-group', page).map(
        (g) => g.querySelector('.option-btn.is-selected')?.dataset.value);
      const complete = selected.every(Boolean);
      const variant = complete
        ? variants.find((v) => selected.every((val, i) => v[`option${i + 1}`] === val))
        : null;
      $$('input[name="id"]', page).forEach((input) => { if (variant) input.value = variant.id; });
      $$('.btn-atc', page).forEach((btn) => {
        if (variant && variant.available) {
          btn.disabled = false; btn.textContent = btn.dataset.addText;
        } else if (variant) {
          btn.disabled = true; btn.textContent = btn.dataset.soldoutText;
        } else {
          btn.disabled = true; btn.textContent = btn.dataset.chooseText;
        }
      });
      if (variant) {
        const priceEl = $('[data-product-price]', page);
        if (priceEl) priceEl.textContent = money(variant.price);
      }
    };

    $$('.option-btn', page).forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-unavailable')) return;
        btn.closest('.option-group__values').querySelectorAll('.option-btn')
          .forEach((x) => x.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        sync();
      });
    });
    sync();

    // Quantity
    $$('.qty', page).forEach((qty) => {
      const input = qty.querySelector('.qty__value');
      qty.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
        input.value = Math.max(1, parseInt(input.value || '1', 10) - 1);
      });
      qty.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
        input.value = parseInt(input.value || '1', 10) + 1;
      });
    });
  }

  /* ------------------------------------------------------------------
     Reveal on scroll
  ------------------------------------------------------------------ */
  function initReveal() {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    $$('[data-reveal]:not(.is-visible)').forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------------------
     Swiper carousels
  ------------------------------------------------------------------ */
  function initSwipers(scope = document) {
    if (typeof Swiper === 'undefined') return;

    $$('.js-hero-swiper', scope).forEach((el) => {
      if (el.swiper) el.swiper.destroy(true, true);
      const autoplay = el.dataset.autoplay !== 'false';
      new Swiper(el, {
        effect: 'fade',
        speed: 1100,
        loop: el.querySelectorAll('.swiper-slide').length > 1,
        autoplay: autoplay ? { delay: parseInt(el.dataset.delay || '5500', 10), disableOnInteraction: false } : false,
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
      });
    });

    $$('.js-collections-swiper', scope).forEach((el) => {
      if (el.swiper) el.swiper.destroy(true, true);
      new Swiper(el, {
        freeMode: true,
        mousewheel: { forceToAxis: true },
        slidesPerView: 1.2,
        spaceBetween: 12,
        breakpoints: { 640: { slidesPerView: 2.2 }, 1024: { slidesPerView: 3.4, spaceBetween: 16 } },
      });
    });

    $$('.js-trending-swiper', scope).forEach((el) => {
      if (el.swiper) el.swiper.destroy(true, true);
      new Swiper(el, {
        freeMode: true,
        loop: true,
        slidesPerView: 1.4,
        spaceBetween: 12,
        speed: 4500,
        autoplay: { delay: 1, disableOnInteraction: false, pauseOnMouseEnter: true },
        breakpoints: { 640: { slidesPerView: 2.4 }, 1024: { slidesPerView: 4.3, spaceBetween: 16 } },
      });
    });

    $$('.js-gallery-swiper', scope).forEach((el) => {
      if (el.swiper) el.swiper.destroy(true, true);
      new Swiper(el, {
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
      });
    });
  }

  /* ------------------------------------------------------------------
     Boot + theme editor support
  ------------------------------------------------------------------ */
  function boot() {
    initHeader();
    initMegaMenu();
    initDrawers();
    initCart();
    initQuickView();
    initWishlist();
    initHotspots();
    initProductPage();
    initReveal();
    initSwipers();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    initSwipers(e.target);
    initProductPage(e.target);
    initReveal();
    paintWishlist();
  });
})();
